import { AsyncPipe } from '@angular/common';
import {
  Component,
  Input,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { SampleDialogComponent } from '@app/modules/service-data/components/sample/sample-dialog/sample-dialog.component';
import { SampleUtilsService } from '@app/modules/service-data/services/sample-utils.service';
import { GetProductInventoryItemListAction } from '@app/modules/service-data/store/product-intentory-item.actions';
import { ProductInventoryItemState } from '@app/modules/service-data/store/product-inventory-item.state';
import {
  GetSamplesListAction,
  RemoveSampleAction,
} from '@app/modules/service-data/store/sample.actions';
import { SampleState } from '@app/modules/service-data/store/sample.state';
import { ServiceDataState } from '@app/modules/service-data/store/service-data.state';
import { SampleStatus } from '@app/modules/service-data/types/sample.types';
import { ServiceData } from '@app/modules/service-data/types/service-data.types';
import { ProductsListState } from '@core/store/products/products.state';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { ConfirmService } from '@shared/components/confirm/confirm-dialog.component';
import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import {
  DataTableActionType,
  DataTableActionsLocation,
  DataTableConfiguration,
  DataTableDataType,
  DataTableSelectionType,
} from '@shared/components/data-table/data-table.types';
import { ToastService } from '@shared/components/toast-container/toast.service';
import { ConfirmCloseReason } from '@shared/shared.types';
import { Observable, of } from 'rxjs';
import { first } from 'rxjs/internal/operators/first';
import { map, shareReplay, switchMap } from 'rxjs/operators';
@Component({
  selector: 'app-samples-list',
  imports: [DataTableComponent, AsyncPipe],
  templateUrl: './samples-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './samples-list.component.scss',
})
export class SamplesListComponent implements OnInit {
  private store = inject(Store);
  private ngbModal = inject(NgbModal);
  private confirmService = inject(ConfirmService);
  private sampleUtils = inject(SampleUtilsService);
  private translateService = inject(TranslateService);
  private toastService = inject(ToastService);

  @Input() readonly = false;

  @Input() serviceDataId: string;
  samples$: Observable<any[]>;
  serviceData$: Observable<ServiceData>;
  requiredSamples$: Observable<number>;

  tableConfiguration: DataTableConfiguration;

  ngOnInit() {
    this.serviceData$ = this.store
      .select(ServiceDataState.getServiceDataById)
      .pipe(map((getById) => getById(this.serviceDataId)));

    this.samples$ = this.serviceData$.pipe(
      map((serviceData) => serviceData.sampleIds),
      switchMap((ids) =>
        this.store
          .dispatch(new GetSamplesListAction({ ids }, {}, null, false))
          .pipe(map(() => ids))
      ),
      switchMap((ids) =>
        this.store
          .select(SampleState.getSamplesByIds)
          .pipe(map((getByIds) => getByIds(ids)))
      ),
      switchMap((samples) =>
        this.sampleUtils.getExtendedSampleList(samples, {
          product: true,
          kit: {},
          serviceData: {},
        })
      ),
      shareReplay(1)
    );

    this.requiredSamples$ = this.serviceData$.pipe(
      switchMap((serviceData) =>
        this.store
          .select(ProductsListState.getBundles)
          .pipe(map((getByIds) => getByIds(serviceData.bundleIds)))
      ),
      map(
        (bundles) => bundles.map((bundle) => bundle.productIds).flat(1).length
      )
    );

    this.tableConfiguration = {
      rows$: this.samples$,
      title: '',
      selectionMode: DataTableSelectionType.NONE,
      actionsLocation: !this.readonly
        ? DataTableActionsLocation.RIGHT
        : DataTableActionsLocation.TOP,
      columns: [
        {
          label: 'serviceData.details.sampleList.columns.kitId',
          property: 'kitId',
        },
        {
          label: 'serviceData.details.sampleList.columns.dateOfVenipuncture',
          property: 'dateOfVenipuncture',
          type: DataTableDataType.DATETIME,
        },
        {
          label: 'serviceData.details.sampleList.columns.product',
          property: 'product.internalName',
        },
        {
          label: 'serviceData.details.sampleList.columns.waybill',
          property: 'shippingInformation.waybill',
          permission: 'waybillInformation',
        },
        {
          label: 'serviceData.details.sampleList.columns.laboratory',
          property: 'kit.laboratory',
          permission: 'laboratory.access',
        },
        {
          label: 'serviceData.details.sampleList.columns.status',
          property: 'status',
        },
      ],
      addAction: !this.readonly
        ? {
            name: 'serviceData.details.sampleList.actions.addSample',
            icon: 'fa-light fa-plus',
            type: DataTableActionType.BUTTON,
            action: this.newSample.bind(this),
            permission: 'serviceData.addSample',
          }
        : null,
      singleActions: !this.readonly
        ? [
            {
              name: 'serviceData.details.sampleList.actions.sampleDetails',
              icon: 'fa-light fa-file',
              type: DataTableActionType.BUTTON,
              action: this.sampleDetails.bind(this),
              permission: 'sample.details',
            },
            {
              name: 'serviceData.details.sampleList.actions.resample',
              icon: 'fa-light fa-droplet',
              type: DataTableActionType.BUTTON,
              disabled: (sample: any) =>
                sample.status !== SampleStatus.forResample,
              action: this.resample.bind(this),
              permission: 'serviceData.addSample',
            },
            {
              name: 'serviceData.details.sampleList.actions.editSample',
              icon: 'fa-light fa-pen',
              type: DataTableActionType.BUTTON,
              action: this.sampleEdit.bind(this),
              permission: 'sample.edit',
            },
            {
              name: 'serviceData.details.sampleList.actions.removeSample',
              icon: 'fa-light fa-xmark',
              type: DataTableActionType.BUTTON,
              action: this.removeSample.bind(this),
              permission: 'serviceData.deleteSample',
            },
          ]
        : null,
    };
  }

  newSample() {
    const modalRef = this.ngbModal.open(SampleDialogComponent, {
      centered: true,
      modalDialogClass: 'modal-size-lg',
    });

    modalRef.componentInstance.serviceDataId = this.serviceDataId;
  }

  sampleDetails(e: Event, sample: any) {
    this.sampleUtils.openDetails(sample.id);
  }

  sampleEdit(e: Event, sample: any) {
    this.sampleUtils.openEdit(sample.id, sample.serviceDataId);
  }

  resample(e: Event, sample: any) {
    this.sampleUtils.openResample(
      sample.id,
      sample.serviceDataId,
      sample.kitId
    );
  }

  removeSample(e: Event, sample: any) {
    this.store
      .dispatch(
        new GetProductInventoryItemListAction(
          { ids: sample.productInventoryItems },
          null,
          false
        )
      )
      .pipe(
        switchMap(() =>
          this.store
            .select(ProductInventoryItemState.getProductInventoryItemsByIds)
            .pipe(
              map((getByIds) => getByIds(sample.productInventoryItems)),
              first(),
              switchMap((piis) => {
                const documents = piis
                  .map((pii) => pii?.documents ?? [])
                  .flat(1);
                const reports = piis.map((pii) => pii?.reports ?? []).flat(1);
                if (!documents.length && !reports.length) {
                  return this.confirmService.confirm({
                    title: this.translateService.instant(
                      'sample.details.remove.title'
                    ),
                    message: this.translateService.instant(
                      'sample.details.remove.message'
                    ),
                  });
                } else {
                  if (documents.length) {
                    this.toastService.warning(
                      this.translateService.instant(
                        'serviceData.details.sampleList.removeSample.documentsAttached'
                      ),
                      this.translateService.instant(
                        'serviceData.details.sampleList.removeSample.cantDeleteTitle'
                      )
                    );
                  }
                  if (reports.length) {
                    this.toastService.warning(
                      this.translateService.instant(
                        'serviceData.details.sampleList.removeSample.reportsAttached'
                      ),
                      this.translateService.instant(
                        'serviceData.details.sampleList.removeSample.cantDeleteTitle'
                      )
                    );
                  }

                  return of(null);
                }
              })
            )
        )
      )
      .subscribe((result) => {
        if (result?.closeReason === ConfirmCloseReason.Yes) {
          this.store.dispatch(new RemoveSampleAction(sample.id));
        }
      });
  }
}
